/**
 * Per-category notification preferences (#1095), stored in the user's
 * settings as `notification_preferences` so they follow the person across
 * devices.
 *
 * - `inApp` off: the category's notifications are hidden from the panel and
 *   do not count towards the unread badge. They stay stored on the server, so
 *   switching it back on shows them again.
 * - `toast` off: no toast when one arrives; the panel still lists it.
 *
 * A notification without a category cannot be switched off — there is no
 * switch that would name it.
 */
import type { Notification, NotificationCategory } from '@/services/notification-service'
import { CATEGORY_ORDER } from './notificationGrouping'
import { notificationCategory } from './notificationPayload'

export type NotificationChannel = 'inApp' | 'toast'

export type NotificationChannels = Record<NotificationChannel, boolean>

export type NotificationPreferences = Record<NotificationCategory, NotificationChannels>

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = ['inApp', 'toast']

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = Object.fromEntries(
  CATEGORY_ORDER.map((category) => [category, { inApp: true, toast: true }]),
) as NotificationPreferences

/** The stored value as preferences: anything missing or not a boolean counts as "on". */
export function parseNotificationPreferences(raw: unknown): NotificationPreferences {
  const stored = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const result = {} as NotificationPreferences
  for (const category of CATEGORY_ORDER) {
    const entry = stored[category] && typeof stored[category] === 'object'
      ? (stored[category] as Record<string, unknown>)
      : {}
    result[category] = {
      inApp: entry.inApp !== false,
      toast: entry.toast !== false,
    }
  }
  return result
}

export function withPreference(
  preferences: NotificationPreferences,
  category: NotificationCategory,
  channel: NotificationChannel,
  enabled: boolean,
): NotificationPreferences {
  return { ...preferences, [category]: { ...preferences[category], [channel]: enabled } }
}

function allows(preferences: NotificationPreferences, notification: Notification, channel: NotificationChannel) {
  const category = notificationCategory(notification)
  return category === undefined || preferences[category][channel]
}

export function showsInApp(preferences: NotificationPreferences, notification: Notification): boolean {
  return allows(preferences, notification, 'inApp')
}

export function showsToast(preferences: NotificationPreferences, notification: Notification): boolean {
  return allows(preferences, notification, 'toast')
}

export function hidesAnyCategory(preferences: NotificationPreferences): boolean {
  return CATEGORY_ORDER.some((category) => !preferences[category].inApp)
}

/** The notifications the panel lists. */
export function visibleNotifications(
  notifications: Notification[],
  preferences: NotificationPreferences,
): Notification[] {
  if (!hidesAnyCategory(preferences)) return notifications
  return notifications.filter((notification) => showsInApp(preferences, notification))
}

/**
 * The unread badge without hidden categories. `serverUnread` is the person's
 * whole count; the hidden unread rows of the loaded list are taken off it.
 * Hidden rows older than the loaded page still count — the list holds the
 * newest 50, which covers what a badge is about.
 */
export function visibleUnreadCount(
  serverUnread: number,
  notifications: Notification[],
  preferences: NotificationPreferences,
): number {
  if (!hidesAnyCategory(preferences)) return serverUnread
  const hiddenUnread = notifications.filter((n) => !n.is_read && !showsInApp(preferences, n)).length
  return Math.max(0, serverUnread - hiddenUnread)
}
